/**
 * Gestione centralizzata degli errori di caricamento dei chunk.
 *
 * Dopo un deploy i chunk hashati referenziati dalla sessione corrente possono non
 * esistere più: l'unico rimedio è ricaricare la pagina per ottenere i nuovi hash.
 * Il rischio opposto è il loop: se l'asset è definitivamente irraggiungibile (rete
 * che lo blocca, file davvero mancante) un reload incondizionato ricarica all'infinito.
 *
 * Qui il reload è consentito una sola volta per sessione. Il flag viene azzerato da
 * `router.afterEach` quando una navigazione va a buon fine.
 */

const RELOAD_KEY = 'chunk-reload';

/** sessionStorage può lanciare se lo storage è disabilitato o pieno. */
function safeSessionStorage(): Storage | null {
	try {
		const s = window.sessionStorage;
		// tocco lo storage: in alcuni browser l'accesso al getter riesce ma le operazioni no
		s.getItem(RELOAD_KEY);
		return s;
	} catch {
		return null;
	}
}

/** Messaggi con cui browser e Vite segnalano un chunk non caricabile. */
const CHUNK_ERROR_PATTERNS = [
	'Failed to fetch dynamically imported module',
	'error loading dynamically imported module',
	'Importing a module script failed',
	'Unable to preload CSS',
	'Loading chunk',
	'Loading CSS chunk',
	'Failed to load module script',
];

export function isChunkLoadError(err: unknown): boolean {
	if (!(err instanceof Error)) {
		return false;
	}
	if (err.name === 'ChunkLoadError') {
		return true;
	}
	return CHUNK_ERROR_PATTERNS.some((pattern) => err.message.includes(pattern));
}

/**
 * Ricarica la pagina, ma solo se non l'abbiamo già fatto per lo stesso motivo.
 *
 * @returns true se il reload è stato avviato, false se era già stato tentato
 *          (in quel caso il chiamante deve mostrare un errore all'utente).
 */
export function reloadOnceForChunkError(): boolean {
	const storage = safeSessionStorage();

	// Senza sessionStorage non c'è modo di ricordare il tentativo precedente:
	// meglio non ricaricare affatto che rischiare un loop infinito.
	if (!storage) {
		return false;
	}

	if (storage.getItem(RELOAD_KEY)) {
		// Già ricaricato per questo errore: non insistiamo.
		//
		// Il flag NON va rimosso qui. Per uno stesso asset mancante questa funzione viene
		// chiamata due volte — prima dal listener `vite:preloadError`, poi dal catch di
		// `lazyLoad` — quindi azzerarlo qui la renderebbe non idempotente: la seconda
		// chiamata lo troverebbe assente e farebbe ripartire il reload, all'infinito.
		// Lo azzera `clearChunkReloadFlag()` da `router.afterEach`, cioè solo quando una
		// navigazione è andata a buon fine.
		return false;
	}

	storage.setItem(RELOAD_KEY, '1');
	window.location.reload();
	return true;
}

/** Da chiamare quando una navigazione è andata a buon fine. */
export function clearChunkReloadFlag(): void {
	safeSessionStorage()?.removeItem(RELOAD_KEY);
}
